<template>
  <div class="mvp-standings">
    <!-- Division Selector (shared for both cards) -->
    <div class="division-selector-wrapper">
      <div class="selector-header">
        <Icon name="mdi:view-dashboard" />
        <h3>Select Division</h3>
      </div>
      <select 
        v-model="selectedDivision" 
        class="division-select-main"
        @change="loadStats"
      >
        <option value="">Choose a division...</option>
        <option 
          v-for="division in divisions" 
          :key="division.id" 
          :value="division.id"
        >
          {{ division.divisionName }}
        </option>
      </select>
    </div>
    
    <!-- 8-Ball MVP Card -->
    <div class="mvp-card">
      <div class="mvp-header">
        <div class="mvp-title">
          <Icon name="mdi:trophy" />
          <h3>8-Ball MVP Standings</h3>
        </div>
      </div>
      
      <div v-if="loading" class="loading-state">
        <Icon name="mdi:loading" class="loading-icon" />
        <p>Loading MVP standings...</p>
      </div>
      
      <div v-else-if="top8BallPlayers.length > 0" class="mvp-list">
        <div 
          v-for="(player, index) in top8BallPlayers" 
          :key="player.id" 
          class="mvp-item"
          :class="{ 'rank-1': index === 0, 'rank-2': index === 1, 'rank-3': index === 2 }"
        >
          <div class="rank-badge" :class="`rank-${index + 1}`">{{ index + 1 }}</div>
          <div class="player-info">
            <div class="player-name">{{ player.playerName }}</div>
            <div class="player-stats">
              <span class="stat-item">
                <Icon name="mdi:chart-line" />
                {{ player.pointsFor }} pts
              </span>
              <span class="stat-item">
                <Icon name="mdi:percent" />
                {{ player.winPercentage }}%
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <div v-else class="no-data">
        <Icon name="mdi:information-outline" />
        <p>{{ selectedDivision ? 'No 8-ball data available' : 'Select a division to view MVP standings' }}</p>
      </div>
    </div>
    
    <!-- 9-Ball MVP Card -->
    <div class="mvp-card">
      <div class="mvp-header">
        <div class="mvp-title">
          <Icon name="mdi:trophy" />
          <h3>9-Ball MVP Standings</h3>
        </div>
      </div>
      
      <div v-if="loading" class="loading-state">
        <Icon name="mdi:loading" class="loading-icon" />
        <p>Loading MVP standings...</p>
      </div>
      
      <div v-else-if="top9BallPlayers.length > 0" class="mvp-list">
        <div 
          v-for="(player, index) in top9BallPlayers" 
          :key="player.id" 
          class="mvp-item"
          :class="{ 'rank-1': index === 0, 'rank-2': index === 1, 'rank-3': index === 2 }"
        >
          <div class="rank-badge" :class="`rank-${index + 1}`">{{ index + 1 }}</div>
          <div class="player-info">
            <div class="player-name">{{ player.playerName }}</div>
            <div class="player-stats">
              <span class="stat-item">
                <Icon name="mdi:chart-line" />
                {{ player.pointsFor }} pts
              </span>
              <span class="stat-item">
                <Icon name="mdi:percent" />
                {{ player.winPercentage }}%
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <div v-else class="no-data">
        <Icon name="mdi:information-outline" />
        <p>{{ selectedDivision ? 'No 9-ball data available' : 'Select a division to view MVP standings' }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { collection, query, where, getDocs, type Firestore } from 'firebase/firestore';

const leagueId = 'abdd3e6f-55de-4d1f-8be0-b2d40139058f';
let db: Firestore | null = null;

interface Division {
  id: string;           // This will be the actual divisionId from Firestore
  divisionName: string; // Display name (base name without 8-Ball/9-Ball)
  eightBallDivisionId?: string;  // Actual division ID for 8-ball variant
  nineBallDivisionId?: string;   // Actual division ID for 9-ball variant
}

interface PlayerStat {
  id: string;
  playerName: string;
  pointsForAgainst: string;  // Format: "15/10" (points scored/points allowed)
  pointsFor: number;         // Parsed from pointsForAgainst (first number)
  winPercentage: number;     // Stored as whole number (e.g., 60 for 60%)
  gamesWon: number;          // Number of games won
}

const divisions = ref<Division[]>([]);
const selectedDivision = ref('');
const top8BallPlayers = ref<PlayerStat[]>([]);
const top9BallPlayers = ref<PlayerStat[]>([]);
const loading = ref(false);

// Load divisions on mount
onMounted(async () => {
  const nuxtApp = useNuxtApp();
  const firebasePlugin = nuxtApp.$firebase as any;

  if (firebasePlugin && firebasePlugin.firestore) {
    db = firebasePlugin.firestore;
  } else {
    console.error('Firebase Firestore not available from Nuxt plugin.');
    return;
  }

  if (db) {
    await loadDivisions();
  }
});

// Load all divisions from teams collection
async function loadDivisions() {
  if (!db) {
    console.error('Firestore not initialized');
    return;
  }

  try {
    const teamsCollectionPath = `leagueStandings/${leagueId}/teams`;
    const teamsQuery = query(collection(db, teamsCollectionPath));
    const querySnapshot = await getDocs(teamsQuery);
    
    // Extract unique divisions by base name (removing 8-Ball/9-Ball suffixes)
    const discoveredDivisionsMap = new Map<string, { 
      baseName: string, 
      eightBallId?: string, 
      nineBallId?: string 
    }>();
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.divisionId && data.divisionName) {
        const divisionId = data.divisionId as string;
        const divisionName = data.divisionName as string;
        
        // Extract base division name
        const baseName = divisionName
          .replace(/\s*8\s*Ball\s*/gi, ' ')
          .replace(/\s*9\s*Ball\s*/gi, ' ')
          .replace(/\s*8-Ball\s*/gi, ' ')
          .replace(/\s*9-Ball\s*/gi, ' ')
          .replace(/\bTPL\s+8\s+/gi, 'TPL ')
          .replace(/\bTPL\s+9\s+/gi, 'TPL ')
          .replace(/\s+8\s+/g, ' ')
          .replace(/\s+9\s+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        
        // Initialize base division if not exists
        if (!discoveredDivisionsMap.has(baseName)) {
          discoveredDivisionsMap.set(baseName, { baseName });
        }
        
        const divisionEntry = discoveredDivisionsMap.get(baseName)!;
        
        // Determine if this is 8-ball or 9-ball variant
        if (divisionName.toLowerCase().includes('8')) {
          divisionEntry.eightBallId = divisionId;
        } else if (divisionName.toLowerCase().includes('9')) {
          divisionEntry.nineBallId = divisionId;
        }
      }
    });

    divisions.value = Array.from(discoveredDivisionsMap.values())
      .map(div => ({
        id: div.baseName,
        divisionName: div.baseName,
        eightBallDivisionId: div.eightBallId,
        nineBallDivisionId: div.nineBallId
      }))
      .sort((a, b) => a.divisionName.localeCompare(b.divisionName));
    
    console.log('Loaded divisions:', divisions.value);
    
    // Auto-select first division if available
    if (divisions.value.length > 0) {
      selectedDivision.value = divisions.value[0].id;
      await loadStats();
    }
  } catch (error) {
    console.error('Error loading divisions:', error);
  }
}

// Load both 8-ball and 9-ball stats for selected division
async function loadStats() {
  if (!db) {
    console.error('Firestore not initialized');
    return;
  }

  if (!selectedDivision.value) {
    top8BallPlayers.value = [];
    top9BallPlayers.value = [];
    return;
  }
  
  loading.value = true;
  try {
    // Find the selected division to get its 8-ball and 9-ball division IDs
    const selectedDiv = divisions.value.find(d => d.id === selectedDivision.value);
    if (!selectedDiv) {
      console.error('Selected division not found');
      loading.value = false;
      return;
    }

    const statsRef = collection(db, 'leagueStandings', leagueId, 'playerStats');
    
    // Fetch 8-ball players using actual divisionId
    let eightBallPlayers: any[] = [];
    if (selectedDiv.eightBallDivisionId) {
      const eightBallQuery = query(statsRef, where('divisionId', '==', selectedDiv.eightBallDivisionId));
      const eightBallSnapshot = await getDocs(eightBallQuery);
      eightBallPlayers = eightBallSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    }
    
    // Fetch 9-ball players using actual divisionId
    let nineBallPlayers: any[] = [];
    if (selectedDiv.nineBallDivisionId) {
      const nineBallQuery = query(statsRef, where('divisionId', '==', selectedDiv.nineBallDivisionId));
      const nineBallSnapshot = await getDocs(nineBallQuery);
      nineBallPlayers = nineBallSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    }
    
    // Sort function - same as StandingsSection
    const sortPlayers = (players: any[]) => {
      return players.sort((a, b) => {
        // Parse points from pointsForAgainst string (e.g., "15/10" -> 15)
        const getPointsFor = (pointsStr: string) => {
          if (!pointsStr || pointsStr === '-') return 0;
          const [pointsFor] = pointsStr.split('/').map(n => parseInt(n) || 0);
          return pointsFor;
        };
        
        const aPoints = getPointsFor(a.pointsForAgainst || '0/0');
        const bPoints = getPointsFor(b.pointsForAgainst || '0/0');
        
        // First priority: Most points for (descending)
        if (bPoints !== aPoints) return bPoints - aPoints;
        
        // Second priority: Win percentage (descending)
        const aWinPct = parseFloat(a.winPercentage || '0');
        const bWinPct = parseFloat(b.winPercentage || '0');
        return bWinPct - aWinPct;
      });
    };
    
    // Map to our interface format
    const mapToPlayerStat = (player: any) => ({
      id: player.id,
      playerName: player.playerName || 'Unknown',
      pointsForAgainst: player.pointsForAgainst || '0/0',
      pointsFor: parsePointsFor(player.pointsForAgainst || '0/0'),
      winPercentage: player.winPercentage || 0,
      gamesWon: player.gamesWon || 0
    });
    
    top8BallPlayers.value = sortPlayers(eightBallPlayers).slice(0, 5).map(mapToPlayerStat);
    top9BallPlayers.value = sortPlayers(nineBallPlayers).slice(0, 5).map(mapToPlayerStat);
    
    console.log('Selected division:', selectedDivision.value);
    console.log('8-ball players:', top8BallPlayers.value);
    if (top8BallPlayers.value.length > 0) {
      console.log('First 8-ball player data:', {
        name: top8BallPlayers.value[0].playerName,
        pointsForAgainst: top8BallPlayers.value[0].pointsForAgainst,
        pointsFor: top8BallPlayers.value[0].pointsFor,
        winPercentage: top8BallPlayers.value[0].winPercentage
      });
    }
    console.log('9-ball players:', top9BallPlayers.value);
  } catch (error) {
    console.error('Error loading stats:', error);
    top8BallPlayers.value = [];
    top9BallPlayers.value = [];
  } finally {
    loading.value = false;
  }
}

// Parse points from "15/10" format
function parsePointsFor(pointsStr: string): number {
  if (!pointsStr || pointsStr === '-') return 0;
  const [pointsFor] = pointsStr.split('/').map(n => parseInt(n) || 0);
  return pointsFor;
}
</script>

<style scoped>
.mvp-standings {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 48px;
  max-width: 100%;
}

.division-selector-wrapper {
  grid-column: 1 / -1; /* Span full width */
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(15px);
  border-radius: 12px;
  border: 1px solid rgba(255, 184, 28, 0.3);
  padding: 16px 20px;
  margin-bottom: 8px;
}

.selector-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  color: var(--gold);
}

.selector-header svg {
  font-size: 1.5rem;
}

.selector-header h3 {
  font-size: 1.1rem;
  font-weight: 700;
  font-family: var(--font-heading);
  margin: 0;
}

.division-select-main {
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 184, 28, 0.4);
  border-radius: 10px;
  padding: 10px 14px;
  color: var(--cream);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.division-select-main:hover {
  border-color: var(--gold);
  background: rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 16px rgba(255, 184, 28, 0.2);
}

.division-select-main:focus {
  outline: none;
  border-color: var(--gold);
  box-shadow: 0 0 0 4px rgba(255, 184, 28, 0.15);
}

.division-select-main option {
  background: var(--navy-blue);
  color: var(--cream);
  padding: 12px;
}

.mvp-card {
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 184, 28, 0.2);
  padding: 16px;
  transition: all 0.3s ease;
  min-width: 0; /* Allow cards to shrink below content width */
}

.mvp-card:hover {
  border-color: rgba(255, 184, 28, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(255, 184, 28, 0.2);
}

.mvp-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.mvp-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--gold);
}

.mvp-title svg {
  font-size: 1.3rem;
}

.mvp-title h3 {
  font-size: 1.05rem;
  font-weight: 700;
  font-family: var(--font-heading);
  margin: 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 16px;
  color: var(--cream);
  opacity: 0.7;
}

.loading-icon {
  font-size: 2rem;
  color: var(--gold);
  margin-bottom: 12px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.loading-state p {
  font-size: 0.95rem;
  margin: 0;
}

.mvp-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mvp-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 184, 28, 0.1);
  transition: all 0.3s ease;
}

.mvp-item:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 184, 28, 0.3);
  transform: translateX(4px);
}

.mvp-item.rank-1 {
  background: rgba(255, 215, 0, 0.1);
  border-color: rgba(255, 215, 0, 0.3);
}

.mvp-item.rank-2 {
  background: rgba(192, 192, 192, 0.1);
  border-color: rgba(192, 192, 192, 0.3);
}

.mvp-item.rank-3 {
  background: rgba(205, 127, 50, 0.1);
  border-color: rgba(205, 127, 50, 0.3);
}

.rank-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.9rem;
  background: rgba(255, 184, 28, 0.2);
  color: var(--gold);
  border: 1px solid rgba(255, 184, 28, 0.3);
  flex-shrink: 0;
}

.rank-badge.rank-1 {
  background: linear-gradient(135deg, #ffd700 0%, #ffed4a 100%);
  color: #1a202c;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
}

.rank-badge.rank-2 {
  background: linear-gradient(135deg, #c0c0c0 0%, #e2e8f0 100%);
  color: #1a202c;
  box-shadow: 0 2px 8px rgba(192, 192, 192, 0.4);
}

.rank-badge.rank-3 {
  background: linear-gradient(135deg, #cd7f32 0%, #d69e2e 100%);
  color: #1a202c;
  box-shadow: 0 2px 8px rgba(205, 127, 50, 0.4);
}

.player-info {
  flex: 1;
  min-width: 0;
}

.player-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--gold);
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.player-stats {
  display: flex;
  gap: 12px;
  font-size: 0.75rem;
  color: var(--cream);
  opacity: 0.9;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.stat-item svg {
  font-size: 0.9rem;
  opacity: 0.7;
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: var(--cream);
  opacity: 0.6;
}

.no-data svg {
  font-size: 2.5rem;
  color: var(--gold);
  margin-bottom: 12px;
  opacity: 0.5;
}

.no-data p {
  font-size: 0.95rem;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .mvp-standings {
    gap: 12px;
  }
  
  .division-selector-wrapper {
    padding: 14px 16px;
    margin-bottom: 12px;
  }
  
  .selector-header {
    gap: 8px;
    margin-bottom: 10px;
  }
  
  .selector-header svg {
    font-size: 1.3rem;
  }
  
  .selector-header h3 {
    font-size: 0.95rem;
  }
  
  .division-select-main {
    font-size: 1rem;
    padding: 12px 16px;
  }
  
  .mvp-card {
    padding: 12px;
  }
  
  .mvp-title h3 {
    font-size: 0.95rem;
  }
  
  .mvp-item {
    padding: 8px;
    gap: 8px;
  }
  
  .rank-badge {
    width: 28px;
    height: 28px;
    font-size: 0.85rem;
  }
  
  .player-name {
    font-size: 0.85rem;
  }
  
  .player-stats {
    font-size: 0.7rem;
    gap: 8px;
  }
  
  .loading-state,
  .no-data {
    padding: 24px 12px;
  }
}

@media (max-width: 480px) {
  .mvp-standings {
    gap: 8px;
  }
  
  .division-selector-wrapper {
    padding: 12px;
  }
  
  .selector-header h3 {
    font-size: 0.9rem;
  }
  
  .division-select-main {
    font-size: 0.85rem;
    padding: 8px 12px;
  }
  
  .mvp-card {
    padding: 10px;
  }
  
  .mvp-title {
    gap: 6px;
  }
  
  .mvp-title svg {
    font-size: 1.1rem;
  }
  
  .mvp-title h3 {
    font-size: 0.85rem;
  }
  
  .mvp-list {
    gap: 6px;
  }
}
</style>
